import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  type ApplicationConfig,
  EnvironmentProviders,
  inject,
  LOCALE_ID,
  Provider,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideAppConfig } from '@move-frontend/portal-toolkit';
import { translocoConfig } from '@partou/config';
import { APP_CONTEXT, type AppContext, AuthInterceptor, MockUserService, UserService } from '@partou/core';
import { ChildrenApiService, DashboardApiService, GroupsApiService, LocationsApiService } from '@partou/my-group/data';
import {
  MockChildrenApiService,
  MockDashboardApiService,
  MockGroupsApiService,
  MockLocationsApiService,
} from '@partou/my-group/mocks';
import { provideAuth, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import { environment } from '../environments/environment';
import { AuthConfig } from './app.auth.config';
import { appRoutes } from './app.routes';

registerLocaleData(localeNl, 'nl-NL');

type ProviderEntry = Provider | EnvironmentProviders;

const realAuthProviders: ProviderEntry[] = [
  provideAuth({ config: AuthConfig }, withAppInitializerAuthCheck()),
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
];

const appContext: AppContext = {
  id: 'my-group',
  apiUrl: environment.apiUrl,
  apiPrefix: environment.apiPrefix,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideTransloco(translocoConfig),
    provideAppInitializer((): ReturnType<TranslocoService['load']> => {
      const transloco: TranslocoService = inject(TranslocoService);
      return transloco.load(transloco.getActiveLang());
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAppConfig({ config: {}, env: environment }),
    provideRouter(appRoutes),
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: APP_CONTEXT, useValue: appContext },
    { provide: ChildrenApiService, useClass: environment.useMocks ? MockChildrenApiService : ChildrenApiService },
    { provide: DashboardApiService, useClass: environment.useMocks ? MockDashboardApiService : DashboardApiService },
    { provide: LocationsApiService, useClass: environment.useMocks ? MockLocationsApiService : LocationsApiService },
    { provide: GroupsApiService, useClass: environment.useMocks ? MockGroupsApiService : GroupsApiService },
    { provide: UserService, useClass: environment.useMocks ? MockUserService : UserService },
    ...(environment.useMocks ? [] : realAuthProviders),
  ],
};
